/**
 * Permission Context
 * Global state management for user permissions and RBAC
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { UserProfile, Permission, PermissionEnum } from "../types/rbac";
import { fetchUserProfile, fetchAllPermissions } from "../api/rbac";
import { useAuth } from "./AuthContext";

interface PermissionContextState {
  userProfile: UserProfile | null;
  permissions: string[]; // User's effective permissions (flattened from roles)
  allPermissions: Permission[]; // All available permissions in system (for admin UI)
  loading: boolean;
  isOwner: boolean; // Quick check if user has * permission
  error: string | null;

  // Content permission shortcuts (commonly used)
  canCreateContent: boolean;
  canReadContent: boolean;
  canUpdateContent: boolean;
  canDeleteContent: boolean;

  // Permission checking methods
  hasPermission: (permission: string | string[]) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;

  // Refresh methods
  refetchProfile: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextState | undefined>(
  undefined,
);

interface PermissionProviderProps {
  children: ReactNode;
}

/**
 * Helper function to check a single permission against user's permissions
 * Supports wildcard matching:
 * - "*" (owner) matches everything
 * - "resource:*" matches "resource:action"
 * - Exact match
 */
function checkSinglePermission(
  required: string,
  userPermissions: string[],
): boolean {
  // Check for owner permission (matches everything)
  if (userPermissions.includes("*")) {
    return true;
  }

  // Check for exact match
  if (userPermissions.includes(required)) {
    return true;
  }

  // Check for resource wildcard (e.g., "content:*" matches "content:read")
  const [resource, action] = required.split(":");
  if (resource && action && userPermissions.includes(`${resource}:*`)) {
    return true;
  }

  return false;
}

export function PermissionProvider({ children }: PermissionProviderProps) {
  const { isAuthenticated: isAuth, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is owner
  const isOwner = permissions.includes("*");

  /**
   * Fetch user profile and permissions
   */
  const loadUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const profile = await fetchUserProfile();
      setUserProfile(profile);

      // Extract effective permissions from roles
      const effectivePerms = new Set<string>();
      profile.roles?.forEach((role) => {
        role.permissions?.forEach((perm) => effectivePerms.add(perm));
      });

      const permArray = Array.from(effectivePerms);
      setPermissions(permArray);

      // If user is owner, fetch all permissions for admin UI
      if (permArray.includes("*")) {
        try {
          const allPerms = await fetchAllPermissions();
          setAllPermissions(allPerms.permissions || []);
        } catch (err) {
          // Could not fetch all permissions - non-critical error, continue
          setError(
            err instanceof Error ? err.message : "Could not fetch permissions",
          );
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load profile");
      // Don't throw - allow app to continue
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load profile when auth state changes
   */
  useEffect(() => {
    if (authLoading) return;

    if (isAuth) {
      loadUserProfile();
    } else {
      setUserProfile(null);
      setPermissions([]);
      setLoading(false);
    }
  }, [isAuth, authLoading, loadUserProfile]);

  /**
   * Check if user has a specific permission (or any of an array of permissions)
   * @param permission - Single permission string or array (OR logic if array)
   */
  const hasPermission = useCallback(
    (permission: string | string[]): boolean => {
      if (loading) return false;
      if (!userProfile) return false;

      const permissionsToCheck = Array.isArray(permission)
        ? permission
        : [permission];

      // OR logic: user needs ANY of the permissions
      return permissionsToCheck.some((perm) =>
        checkSinglePermission(perm, permissions),
      );
    },
    [loading, userProfile, permissions],
  );

  /**
   * Check if user has ANY of the provided permissions (OR logic)
   * @param permissions - Array of permission strings
   */
  const hasAnyPermission = useCallback(
    (permissionsToCheck: string[]): boolean => {
      return hasPermission(permissionsToCheck);
    },
    [hasPermission],
  );

  /**
   * Check if user has ALL of the provided permissions (AND logic)
   * @param permissions - Array of permission strings
   */
  const hasAllPermissions = useCallback(
    (permissionsToCheck: string[]): boolean => {
      if (loading) return false;
      if (!userProfile) return false;

      // AND logic: user needs EVERY permission
      return permissionsToCheck.every((perm) =>
        checkSinglePermission(perm, permissions),
      );
    },
    [loading, userProfile, permissions],
  );

  /**
   * Refetch user profile (useful after role changes)
   */
  const refetchProfile = useCallback(async () => {
    await loadUserProfile();
  }, [loadUserProfile]);

  // Computed content permission shortcuts
  const canCreateContent = useMemo(
    () =>
      hasPermission([
        PermissionEnum.CONTENT_CREATE,
        PermissionEnum.CONTENT_ALL,
      ]),
    [hasPermission],
  );
  const canReadContent = useMemo(
    () =>
      hasPermission([PermissionEnum.CONTENT_READ, PermissionEnum.CONTENT_ALL]),
    [hasPermission],
  );
  const canUpdateContent = useMemo(
    () =>
      hasPermission([
        PermissionEnum.CONTENT_UPDATE,
        PermissionEnum.CONTENT_ALL,
      ]),
    [hasPermission],
  );
  const canDeleteContent = useMemo(
    () =>
      hasPermission([
        PermissionEnum.CONTENT_DELETE,
        PermissionEnum.CONTENT_ALL,
      ]),
    [hasPermission],
  );

  const value: PermissionContextState = {
    userProfile,
    permissions,
    allPermissions,
    loading,
    isOwner,
    error,
    canCreateContent,
    canReadContent,
    canUpdateContent,
    canDeleteContent,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refetchProfile,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook to use permission context
 * Must be used within PermissionProvider
 */
export function usePermissions(): PermissionContextState {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionProvider");
  }
  return context;
}
